/**
 * Import d'un export **Passman** JSON (gestionnaire de mots de passe de
 * Nextcloud, « Parametres du coffre > Exporter > JSON ») : ses utilisateurs,
 * souvent sur un Nextcloud auto-heberge, doivent pouvoir partir avec TOUT leur
 * coffre.
 *
 * Purement local, aucune E/S reseau : ce module transforme un export en
 * `ImportedEntry`. Le fichier est un tableau d'identifiants
 * `{ label, username, email, password, url, description, tags, otp,
 * custom_fields, files, delete_time, ... }`. Passman n'a pas de dossiers : ses
 * `tags` (`{ text }`) sont repris en etiquettes. Un identifiant mis a la
 * corbeille (`delete_time` > 0) n'est pas importe.
 *
 * Les fichiers joints de Passman sont chiffres a part sur le serveur Nextcloud
 * et l'export ne porte que leur nom : ils sont nommes dans les notes, jamais
 * perdus en silence.
 */
import type { EntryCustomField } from '@klavox/crypto-core';
import { csvColumnField, joinNotes, normalizeTotpSecret, type ImportedEntry } from './csv-import.js';
import { normalizeTags } from './entry-tags.js';
import { serverHost } from './typed-fields.js';

type Json = unknown;

function asObject(value: Json): Record<string, Json> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, Json>) : null;
}

function asArray(value: Json): readonly Json[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: Json): string {
  return typeof value === 'string' ? value.trim() : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
}

/** Valeur brute d'un secret : jamais rognee, une espace peut en faire partie. */
function rawSecret(value: Json): string {
  return typeof value === 'string' ? value : asString(value);
}

/**
 * Cles reprises par le lecteur, et metadonnees internes de Passman sans valeur
 * pour l'utilisateur (identifiants, dates, partage, icone). Toute AUTRE cle non
 * vide devient un champ personnalise, jamais perdue.
 */
const KNOWN_KEYS = new Set([
  'label',
  'username',
  'email',
  'password',
  'url',
  'description',
  'tags',
  'otp',
  'custom_fields',
  'files',
  'delete_time',
  'credential_id',
  'id',
  'guid',
  'vault_id',
  'user_id',
  'created',
  'changed',
  'expire_time',
  'renew_interval',
  'favicon',
  'icon',
  'hidden',
  'shared_key',
  'compromised',
  'acl',
  'set_share_access',
]);

/** Nom d'un fichier joint Passman (`{ filename, guid, size, mimetype }`). */
function fileName(value: Json): string {
  const file = asObject(value);
  return file === null ? '' : asString(file['filename']);
}

/**
 * Graine TOTP de l'objet `otp` : l'URI `otpauth://` du QR code d'abord (elle
 * garde chiffres, periode et algorithme), sinon `secret`. Une graine qui n'est
 * pas du base32 reste un champ masque, jamais un TOTP faux.
 */
function totpOf(raw: Json): { secret: string; extraNote: string; field: EntryCustomField | null } {
  const otp = asObject(raw);
  if (otp === null) return { secret: '', extraNote: '', field: null };
  const qrData = asString(asObject(otp['qr_uri'])?.['qrData']);
  if (/^otpauth:\/\//iu.test(qrData)) return { ...normalizeTotpSecret(qrData), field: null };
  const secret = asString(otp['secret']);
  if (secret.length === 0) return { secret: '', extraNote: '', field: null };
  if (!/^otpauth:\/\//iu.test(secret) && !/^[A-Z2-7]+=*$/u.test(secret.replace(/[\s-]+/gu, '').toUpperCase())) {
    return { secret: '', extraNote: '', field: { name: 'Cle a deux facteurs', value: secret, hidden: true } };
  }
  return { ...normalizeTotpSecret(secret), field: null };
}

/**
 * Date du mot de passe : Passman ne date que l'identifiant ; `changed`
 * (secondes, reecrit a chaque mise a jour) = borne haute, jamais `created`.
 * Aucune date sans mot de passe ; absente ou illisible : l'import vaut pose.
 */
function passmanPasswordChangedAt(credential: Record<string, Json>, password: string): { passwordChangedAt?: number } {
  const changed = password.length > 0 ? Number(credential['changed']) : NaN;
  return Number.isFinite(changed) && changed > 0 ? { passwordChangedAt: changed * 1000 } : {};
}

/** Un identifiant Passman -> une entree (identifiant, ou note s'il n'a aucun secret). */
function parsePassmanCredential(credential: Record<string, Json>): ImportedEntry | null {
  if (Number(credential['delete_time']) > 0) return null;
  const label = asString(credential['label']);
  const email = asString(credential['email']);
  let username = asString(credential['username']);
  const usernameIsEmail = username.length === 0 && email.length > 0;
  if (usernameIsEmail) username = email;
  const password = rawSecret(credential['password']);
  const url = asString(credential['url']);
  const uris = url.length > 0 ? [url] : [];
  const totp = totpOf(credential['otp']);

  const customFields: EntryCustomField[] = [];
  if (!usernameIsEmail && email.length > 0 && email !== username) customFields.push({ name: 'E-mail', value: email, hidden: false });
  if (totp.field !== null) customFields.push(totp.field);
  const missingFiles = asArray(credential['files']).map(fileName).filter((name) => name.length > 0);
  for (const raw of asArray(credential['custom_fields'])) {
    const field = asObject(raw);
    if (field === null) continue;
    const name = asString(field['label']);
    const fieldType = asString(field['field_type']).toLowerCase();
    // Champ de type fichier : son contenu n'est pas dans l'export, il est nomme dans les notes.
    if (fieldType === 'file') {
      const attached = fileName(field['value']);
      if (attached.length > 0) missingFiles.push(attached);
      continue;
    }
    const value = rawSecret(field['value']);
    if (value.trim().length === 0) continue;
    // `field_type: password` ou drapeau `secret` : masque quel que soit le libelle.
    const hidden = fieldType === 'password' || field['secret'] === true || csvColumnField(name, value).hidden;
    customFields.push({ name: name || 'Champ', value, hidden });
  }
  for (const [key, value] of Object.entries(credential)) {
    if (KNOWN_KEYS.has(key) || asObject(value) !== null || Array.isArray(value)) continue;
    const text = rawSecret(value);
    if (text.trim().length > 0) customFields.push(csvColumnField(key, text));
  }

  // La note garde sa mise en forme (retraits, sauts de ligne), seuls ses bords sont rognes.
  const description = typeof credential['description'] === 'string' ? credential['description'].replace(/\r\n?/gu, '\n').trim() : '';
  const notes = joinNotes([
    description,
    totp.extraNote,
    missingFiles.length > 0 ? `Fichier(s) absent(s) de l'export Passman : ${missingFiles.join(', ')}` : '',
  ]);
  const tags = normalizeTags(asArray(credential['tags']).map((tag) => asString(asObject(tag)?.['text'] ?? tag)));
  // Ni identifiant, ni mot de passe, ni adresse, ni TOTP : c'est une note securisee.
  const isNote = username.length === 0 && password.length === 0 && uris.length === 0 && totp.secret.length === 0;
  if (isNote && label.length === 0 && notes.length === 0 && customFields.length === 0) return null;
  const host = url.length > 0 ? serverHost(url).replace(/^www\./u, '') : '';
  return {
    type: isNote ? 'note' : 'login',
    name: label || host || username || 'Sans nom',
    username,
    password,
    uris,
    notes,
    totpSecret: totp.secret,
    favorite: false,
    ...passmanPasswordChangedAt(credential, password),
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(tags.length > 0 ? { tags } : {}),
  };
}

/**
 * Un export Passman se reconnait a un tableau d'objets dont CHACUN porte un
 * `label` texte et au moins une cle propre a Passman (`custom_fields`, `otp`,
 * `files`, `guid`, `credential_id`, `vault_id`) : aucun autre format lu par
 * Klavox n'est un tableau a la racine.
 */
export function isPassmanExport(data: Json): boolean {
  if (!Array.isArray(data) || data.length === 0) return false;
  return data.every((raw) => {
    const credential = asObject(raw);
    return (
      credential !== null &&
      typeof credential['label'] === 'string' &&
      ['custom_fields', 'otp', 'files', 'guid', 'credential_id', 'vault_id'].some((key) => key in credential)
    );
  });
}

/** Parse un export Passman JSON : chaque identifiant hors corbeille devient une entree. */
export function parsePassmanExport(data: Json): ImportedEntry[] {
  if (!Array.isArray(data)) throw new Error('export Passman : structure inattendue');
  return data
    .map(asObject)
    .filter((credential): credential is Record<string, Json> => credential !== null)
    .map(parsePassmanCredential)
    .filter((entry): entry is ImportedEntry => entry !== null);
}
